// Single source of truth for SaaS subscription plans (frontend side).
//
// Mirrors the catalog seeded in /app/backend/seed.py and exposed via
// GET /api/billing/plans. Use this file for: pricing page, owner dashboard,
// signup, billing UI, feature gating. NEVER hardcode plan names / prices in
// components — always import from here.
//
// Three lookups:
//   PLAN_ID    backend DB id     ("restaurant_starter" | "restaurant_business" | ...)
//   PLAN_TIER  marketing tier    ("starter"            | "professional"        | ...)
//
// The two are different namespaces because the backend was bootstrapped with
// per-business plan ids before the marketing rebranding. Keep both mappings
// up to date here. When Stripe is migrated to LIVE, fill stripe_price_key.

import { Star, Crown } from "lucide-react";

/**
 * Master catalog.
 * Keys are the canonical TIER ids used by the marketing/pricing layer and
 * by `/api/billing/plans`. The `db_plan_id` field maps to the backend DB.
 */
export const PLANS_CATALOG = {
  starter: {
    id: "starter",
    aliases: ["starter_restaurant"],
    db_plan_id: "restaurant_starter",
    name: "Starter Restaurant",
    short_name: "Starter",
    tagline: "Per ristoranti che vogliono iniziare a controllare i numeri.",
    description: "Per ristoranti che muovono i primi passi.",
    target_segment: "restaurant",
    price_monthly_eur: 49,
    currency: "EUR",
    billing_period: "monthly",
    trial_days: 14,
    accent: "#102A43",
    icon: null,
    badge: null,
    featured: false,
    modules: [
      "restaurant_core",
      "pos",
      "reservations",
      "online_reservations",
      "takeaway",
      "payments",
      "inventory",
      "recipes",
      "food_cost",
      "notifications",
      "reports",
    ],
    bullets: [
      "POS, comande termiche, KDS",
      "Gestione tavoli, sale, menu",
      "Prenotazioni online (fino a 50/mese)",
      "Ordini takeaway",
      "Magazzino e ricette base",
      "Food cost base",
      "1 sede · fino a 5 utenti",
    ],
    limits: { locations: 1, users: 5 },
    enterprise: false,
    custom_pricing: false,
    stripe_product_key: "PRODUCT_ID_STARTER",
    stripe_price_key: "PRICE_ID_STARTER",
  },

  professional: {
    id: "professional",
    aliases: ["professional_restaurant"],
    db_plan_id: "restaurant_business",
    name: "Professional Restaurant",
    short_name: "Professional",
    tagline: "La scelta della maggior parte dei ristoratori strutturati.",
    description:
      "Piano completo per ristoranti strutturati: food cost, magazzino, ricette, marginalità, AI Controller e Profit Recovery.",
    target_segment: "restaurant",
    price_monthly_eur: 89,
    currency: "EUR",
    billing_period: "monthly",
    trial_days: 14,
    accent: "#0D9488",
    icon: Star,
    badge: "Più scelto",
    featured: true,
    modules: [
      "restaurant_core",
      "pos",
      "reservations",
      "online_reservations",
      "takeaway",
      "delivery",
      "payments",
      "inventory",
      "recipes",
      "food_cost",
      "waste_control",
      "margin_analysis",
      "break_even",
      "ai_controller",
      "profit_recovery",
      "reports",
      "notifications",
    ],
    bullets: [
      "Tutto Starter Restaurant +",
      "Prenotazioni online illimitate · link booking personalizzato",
      "Magazzino multi-deposito",
      "Food cost & marginalità avanzati",
      "Controllo sprechi · Break-even",
      "AI Financial Controller + Profit Recovery",
      "Report avanzati · notifiche operative",
      "1 sede · utenti illimitati",
    ],
    limits: { locations: 1, users: null /* unlimited */ },
    enterprise: false,
    custom_pricing: false,
    stripe_product_key: "PRODUCT_ID_PROFESSIONAL",
    stripe_price_key: "PRICE_ID_PROFESSIONAL",
  },

  hotel_starter: {
    id: "hotel_starter",
    db_plan_id: "hotel_starter",
    name: "Hotel Starter",
    short_name: "Hotel Starter",
    tagline: "Per hotel e B&B che vogliono digitalizzare le camere.",
    description: "Per hotel e strutture ricettive che muovono i primi passi.",
    target_segment: "hotel",
    price_monthly_eur: 79,
    currency: "EUR",
    billing_period: "monthly",
    accent: "#102A43",
    icon: null,
    badge: null,
    featured: false,
    // SSOT mirror — Hotel Starter does NOT include business_analytics
    // (enterprise-only). Phase 12.1 strict gating.
    modules: [
      "hotel_core",
      "reservations",
      "payments",
      "reports",
      "notifications",
    ],
    bullets: [
      "Planner camere e calendario operativo",
      "Prenotazioni & pagamenti",
      "Check-in / Check-out digitali",
      "Schede alloggiati Questura",
      "1 struttura · fino a 30 camere",
    ],
    limits: { locations: 1, rooms: 30 },
    enterprise: false,
    custom_pricing: false,
    stripe_product_key: "PRODUCT_ID_HOTEL_STARTER",
    stripe_price_key: "PRICE_ID_HOTEL_STARTER",
  },

  hotel_pro: {
    id: "hotel_pro",
    db_plan_id: "hotel_business",
    name: "Hotel Professional",
    short_name: "Hotel Professional",
    tagline: "Hotel strutturati con compliance, KPI operativi e night audit.",
    description: "Suite hospitality completa per hotel strutturati.",
    target_segment: "hotel",
    price_monthly_eur: 149,
    currency: "EUR",
    billing_period: "monthly",
    accent: "#0D9488",
    icon: Star,
    badge: null,
    featured: true,
    // SSOT mirror — Hotel Professional has NO business_analytics (Enterprise-only).
    modules: [
      "hotel_core",
      "reservations",
      "payments",
      "breakfast_management",
      "revenue_management",
      "room_service",
      "reports",
      "crm_marketing",
      "notifications",
    ],
    bullets: [
      "Tutto Hotel Starter +",
      "Centro Operativo Live",
      "Dashboard KPI (occupancy/ADR/RevPAR)",
      "Night Audit & chiusura giornaliera",
      "Pagamenti avanzati e reportistica",
    ],
    limits: { locations: 1, rooms: null },
    enterprise: false,
    custom_pricing: false,
    stripe_product_key: "PRODUCT_ID_HOTEL_PRO",
    stripe_price_key: "PRICE_ID_HOTEL_PRO",
  },

  combo: {
    id: "combo",
    db_plan_id: "combo_premium",
    name: "Combo Premium",
    short_name: "Combo",
    tagline: "Hotel con ristorazione. POS + PMS unificati.",
    description: "Per hotel con ristorante integrato.",
    target_segment: "combo",
    price_monthly_eur: 299,
    currency: "EUR",
    billing_period: "monthly",
    accent: "#0F766E",
    icon: Crown,
    badge: "Best value",
    featured: true,
    // SSOT mirror — Combo Premium = Hotel Professional ∪ Professional Restaurant + combo_integration.
    // ZERO enterprise-only modules (no multi_location, no benchmark, no exec_financial, no BI).
    modules: [
      "restaurant_core",
      "hotel_core",
      "combo_integration",
      "pos",
      "reservations",
      "online_reservations",
      "takeaway",
      "delivery",
      "payments",
      "inventory",
      "recipes",
      "food_cost",
      "waste_control",
      "margin_analysis",
      "break_even",
      "ai_controller",
      "profit_recovery",
      "breakfast_management",
      "revenue_management",
      "room_service",
      "crm_marketing",
      "reports",
      "notifications",
    ],
    bullets: [
      "Tutto Hotel Professional + Professional Restaurant",
      "Conto camera + F&B unico",
      "Breakfast & room service",
      "Margine per ospite reale",
      "Eventi, gruppi, banchetti",
    ],
    limits: { locations: 1, users: null, rooms: null },
    enterprise: false,
    custom_pricing: false,
    stripe_product_key: "PRODUCT_ID_COMBO",
    stripe_price_key: "PRICE_ID_COMBO",
  },

  enterprise: {
    id: "enterprise",
    aliases: ["enterprise_restaurant"],
    db_plan_id: "enterprise",
    name: "Enterprise",
    short_name: "Enterprise",
    tagline: "Gruppi multi-sede, catene e franchising. Onboarding e supporto dedicati.",
    description: "Per gruppi, catene e strutture multi-location.",
    target_segment: "enterprise",
    price_monthly_eur: 499,
    currency: "EUR",
    billing_period: "monthly",
    trial_days: null,  // custom for enterprise
    accent: "#0B1B2C",
    icon: Crown,
    badge: "Enterprise",
    featured: false,
    // SSOT mirror — Enterprise = Combo Premium + AI forecast + business
    // analytics + the 5 enterprise-only modules.
    modules: [
      "restaurant_core",
      "hotel_core",
      "combo_integration",
      "pos",
      "reservations",
      "online_reservations",
      "takeaway",
      "delivery",
      "payments",
      "inventory",
      "recipes",
      "food_cost",
      "waste_control",
      "margin_analysis",
      "break_even",
      "ai_controller",
      "profit_recovery",
      "breakfast_management",
      "revenue_management",
      "room_service",
      "crm_marketing",
      "reports",
      "notifications",
      "ai_forecast",
      "business_analytics",
      // Enterprise-only (Phase 12.1)
      "multi_location_dashboard",
      "benchmark",
      "audit_trail_enterprise",
      "executive_financial",
      "intelligence_bi",
    ],
    bullets: [
      "Dashboard di gruppo centralizzata",
      "Benchmark fra sedi + ruoli granulari",
      "SSO, audit log append-only e API",
      "Account manager dedicato",
      "Onboarding strutturato + formazione",
      "SLA e livelli di servizio definiti nel contratto Enterprise",
    ],
    limits: { locations: null, users: null, rooms: null },
    enterprise: true,
    custom_pricing: true,   // Display "Da €499 / personalizzato"
    stripe_product_key: "PRODUCT_ID_ENTERPRISE",
    stripe_price_key: "PRICE_ID_ENTERPRISE",
  },
};

/**
 * Look up a plan by EITHER its marketing tier id or its backend DB id.
 * Returns null when nothing matches (safe for components).
 */
export function getPlan(planIdOrTier) {
  if (!planIdOrTier) return null;
  if (PLANS_CATALOG[planIdOrTier]) return PLANS_CATALOG[planIdOrTier];
  // Fallback: search by db_plan_id OR alias
  return (
    Object.values(PLANS_CATALOG).find(
      (p) =>
        p.db_plan_id === planIdOrTier ||
        (Array.isArray(p.aliases) && p.aliases.includes(planIdOrTier))
    ) || null
  );
}

/**
 * Resolve the canonical TIER from any plan identifier (tier or db_plan_id).
 */
export function getPlanTier(planIdOrTier) {
  return getPlan(planIdOrTier)?.id || null;
}

/**
 * Return the user-facing plan name resolved from any identifier.
 * Useful as a drop-in replacement for `plan.name` in components.
 */
export function getPlanName(planIdOrTier, fallback = "—") {
  return getPlan(planIdOrTier)?.name || fallback;
}

/**
 * Feature gating helper. Returns true when the plan includes the given
 * module id. Pass either a tier ("professional") or a db_plan_id
 * ("restaurant_business") — both work.
 *
 *   hasFeature("professional", "food_cost")        → true
 *   hasFeature("restaurant_business", "food_cost") → true
 *   hasFeature("starter", "food_cost")             → false
 */
export function hasFeature(planIdOrTier, moduleId) {
  const plan = getPlan(planIdOrTier);
  if (!plan) return false;
  return plan.modules.includes(moduleId);
}

/**
 * Plans filtered by target audience (restaurant / hotel / combo / enterprise).
 * Useful for the signup flow & pricing page filters.
 */
export function getPlansByTarget(target) {
  if (!target) return Object.values(PLANS_CATALOG);
  return Object.values(PLANS_CATALOG).filter(
    (p) => p.target_segment === target
  );
}

/**
 * Public canonical list (preserves declaration order).
 */
export const PLANS_LIST = Object.values(PLANS_CATALOG);
